import './Home.css';
import { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useSeo from '../../seo/useSeo';
import * as PortOne from '@portone/browser-sdk/v2';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../supabaseClient';
import Header from '../../components/Header';
import Footer from '../../components/Footer';
import Login from '../../components/Login';
import PhoneNumberPopup from '../../components/PhoneNumberPopup';

// 포트원(PortOne) v2 — KG이니시스 정기결제(빌링키).
// 공개 식별자(Store ID / Channel Key)만 클라이언트에서 사용하며, 값은 .env 에서 주입.
const PORTONE_STORE_ID = process.env.REACT_APP_PORTONE_STORE_ID;
// 정기결제(빌링) 채널 — KG이니시스 INIBillTst. 실제 청구 금액은 서버(/api/portone/billing)가 결정.
const PORTONE_CHANNEL_KEY = process.env.REACT_APP_PORTONE_CHANNEL_KEY;
// 정기결제창에 '표시용'으로 노출할 금액(월 구독료). SDK displayAmount 로 결제창에 금액 표기.
const PORTONE_DISPLAY_AMOUNT = 12000; // KRW

// 카드사 심사용 임시: 연간결제 숨김(월간만 노출). Polar 복귀 시 true 로 되살리면 원복.
const SHOW_YEARLY = false;

// 카카오톡 문의 채널 (Footer 문의 링크와 동일)
const KAKAO_CONTACT_URL = 'http://pf.kakao.com/_KQrhn';

/* 컬러 이모지 대신 쓰는 라인 아이콘들 (stroke=currentColor, 색은 CSS에서 지정) */
const iconBaseProps = {
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 1.8,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
  focusable: false,
};

function ClockIcon() {
  return (
    <svg {...iconBaseProps}>
      <circle cx="12" cy="12" r="9" />
      <path d="M12 7.2v5l3.4 2" />
    </svg>
  );
}

function EyeIcon() {
  return (
    <svg {...iconBaseProps}>
      <path d="M2.5 12S6 5.5 12 5.5 21.5 12 21.5 12 18 18.5 12 18.5 2.5 12 2.5 12z" />
      <circle cx="12" cy="12" r="3" />
    </svg>
  );
}

function LaptopIcon() {
  return (
    <svg {...iconBaseProps}>
      <rect x="4" y="5" width="16" height="11" rx="2" />
      <path d="M2.2 20h19.6" />
    </svg>
  );
}

function BuildingIcon() {
  return (
    <svg {...iconBaseProps}>
      <path d="M6 21V4.5A1.5 1.5 0 0 1 7.5 3h6A1.5 1.5 0 0 1 15 4.5V21" />
      <path d="M15 9h2.5A1.5 1.5 0 0 1 19 10.5V21" />
      <line x1="3.5" y1="21" x2="20.5" y2="21" />
      <line x1="9" y1="7" x2="12" y2="7" />
      <line x1="9" y1="11" x2="12" y2="11" />
      <line x1="9" y1="15" x2="12" y2="15" />
    </svg>
  );
}

function RocketIcon() {
  return (
    <svg {...iconBaseProps}>
      <path d="M4.5 16.5c-1.5 1.26-2 5-2 5s3.74-.5 5-2c.71-.84.7-2.13-.09-2.91a2.18 2.18 0 0 0-2.91-.09z" />
      <path d="M12 15l-3-3a22 22 0 0 1 2-3.95A12.88 12.88 0 0 1 22 2c0 2.72-.78 7.5-6 11a22.35 22.35 0 0 1-4 2z" />
      <path d="M9 12H4s.55-3.03 2-4c1.62-1.08 5 0 5 0" />
      <path d="M12 15v5s3.03-.55 4-2c1.08-1.62 0-5 0-5" />
    </svg>
  );
}

/* 생태계 섹션(출시 예정 서비스) 아이콘 */
function BriefcaseIcon() {
  return (
    <svg {...iconBaseProps}>
      <rect x="3" y="7" width="18" height="13" rx="2" />
      <path d="M8 7V5.5A1.5 1.5 0 0 1 9.5 4h5A1.5 1.5 0 0 1 16 5.5V7" />
      <path d="M3 12.5h18" />
    </svg>
  );
}

function TagIcon() {
  return (
    <svg {...iconBaseProps}>
      <path d="M20.6 13.4 13.4 20.6a1.98 1.98 0 0 1-2.8 0l-7.2-7.2A2 2 0 0 1 2.8 12V5a2 2 0 0 1 2-2h7a2 2 0 0 1 1.4.6l7.4 7.4a1.98 1.98 0 0 1 0 2.4z" />
      <circle cx="7.3" cy="7.3" r="1.3" />
    </svg>
  );
}

function GraduationCapIcon() {
  return (
    <svg {...iconBaseProps}>
      <path d="M22 10 12 5 2 10l10 5 10-5z" />
      <path d="M6 12v4.5c0 1.1 2.7 2.5 6 2.5s6-1.4 6-2.5V12" />
      <path d="M22 10v5" />
    </svg>
  );
}

function TargetIcon() {
  return (
    <svg {...iconBaseProps}>
      <circle cx="12" cy="12" r="9" />
      <circle cx="12" cy="12" r="5" />
      <circle cx="12" cy="12" r="1.3" />
    </svg>
  );
}

function GeminiPage() {
  const { t } = useTranslation();

  // FAQ 구조화 데이터 — 검색 결과에 질문/답변이 펼쳐 노출될 수 있게 한다.
  // 문구는 화면에 실제로 보이는 FAQ 와 같은 키를 쓰므로 언어 전환에도 함께 따라간다.
  const faqJsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: [1, 2, 3, 4, 5].map((n) => ({
        '@type': 'Question',
        name: t(`home.faq.q${n}`),
        acceptedAnswer: { '@type': 'Answer', text: t(`home.faq.a${n}`) },
      })),
    }),
    [t],
  );
  useSeo({ path: '/', jsonLd: faqJsonLd });

  const [openFaq, setOpenFaq] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  const [billingInterval, setBillingInterval] = useState(SHOW_YEARLY ? 'yearly' : 'monthly'); // 'monthly' or 'yearly'
  const [subscription, setSubscription] = useState(null);
  const [showPhonePopup, setShowPhonePopup] = useState(false);
  const { user, profile } = useAuth();
  const navigate = useNavigate();

  // 로그인한 사용자의 구독 상태 조회 (구독 중이면 추가 구매를 막기 위함)
  useEffect(() => {
    if (!user) {
      setSubscription(null);
      return;
    }
    const fetchSubscription = async () => {
      try {
        const { data } = await supabase
          .from('subscriptions')
          .select('*')
          .eq('user_id', user.id)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        setSubscription(data || null);
      } catch (e) {
        // 무시 — 구독 정보 조회 실패 시 일반 결제 흐름 유지
      }
    };
    fetchSubscription();
  }, [user]);

  // 모바일 리다이렉트 복귀 처리 (?portone=billing&billingKey=...&code=...)
  useEffect(() => {
    if (!user) return;
    const params = new URLSearchParams(window.location.search);
    if (params.get('portone') !== 'billing') return;
    const billingKey = params.get('billingKey');
    const code = params.get('code');
    window.history.replaceState({}, '', window.location.pathname);
    if (code) {
      alert(params.get('message') || t('home.pricing.testPayFailed'));
      return;
    }
    if (billingKey) activateBilling(billingKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  // Polar checkout URLs
  // Production URLs (실제 결제)
  const POLAR_CHECKOUT_URLS = {
    monthly: 'https://buy.polar.sh/polar_cl_VL1ZYMzG2lRGZ54EAQFqt0xIKPw6vDUt7JBXb1e4dcb',
    yearly: 'https://buy.polar.sh/polar_cl_8653rK7SholvZNMtuewox4L8Uz3TETZZ1zwLQ1IHT0a',
  };

  // Sandbox URLs (테스트용)
  // const POLAR_CHECKOUT_URLS = {
  //   monthly: 'https://sandbox-api.polar.sh/v1/checkout-links/polar_cl_yfYiO3VD5yqy4so1i1xxevWyJ5OWwrc4pAaXj2487UL/redirect',
  //   yearly: 'https://sandbox-api.polar.sh/v1/checkout-links/polar_cl_9Cx8jV7dQtndHGq8pOE2sO67eLk5Re6qrLOof1S2yB8/redirect'
  // };

  const handlePremiumClick = () => {
    if (!user) {
      setShowLogin(true);
      return;
    }

    // 이미 구독 중이면 추가 구매를 막고, 플랜 변경은 카카오톡 문의로 안내
    const isSubscribed =
      subscription?.status === 'active' || subscription?.cancel_at_period_end === true;
    if (isSubscribed) {
      const goKakao = window.confirm(t('home.pricing.alreadySubscribed'));
      if (goKakao) {
        window.open(KAKAO_CONTACT_URL, '_blank', 'noopener,noreferrer');
      }
      return;
    }

    const checkoutUrl =
      billingInterval === 'monthly' ? POLAR_CHECKOUT_URLS.monthly : POLAR_CHECKOUT_URLS.yearly;

    // 결제 완료 후 프로필 페이지로 리다이렉트
    const successUrl = `${window.location.origin}/profile?success=true`;
    const urlWithParams = `${checkoutUrl}?customer_email=${encodeURIComponent(user.email)}&success_url=${encodeURIComponent(successUrl)}`;

    window.location.href = urlWithParams;
  };

  const [payTestSubmitting, setPayTestSubmitting] = useState(false);

  // 포트원 KG이니시스 정기결제(빌링키). 휴대폰 번호가 없으면 먼저 팝업으로 입력받고(1회만),
  // 저장돼 있으면 팝업 없이 바로 결제.
  const handlePortonePayTest = async () => {
    if (!user) {
      alert(t('home.pricing.testPayLoginRequired'));
      setShowLogin(true);
      return;
    }
    if (!PORTONE_STORE_ID || !PORTONE_CHANNEL_KEY) {
      alert(t('home.pricing.testPayNotConfigured'));
      return;
    }
    // 이미 구독 중(Polar 등)이면 결제 시작하지 않고, 현재 구독 종료 후 진행하도록 안내
    const isSubscribed =
      subscription?.status === 'active' || subscription?.cancel_at_period_end === true;
    if (isSubscribed) {
      alert(t('home.pricing.portoneAlreadySub'));
      return;
    }
    const phone = profile?.phone_number;
    if (!phone) {
      setShowPhonePopup(true);
      return;
    }
    await runPayTest(phone);
  };

  // 발급된 빌링키를 서버로 보내 즉시 결제 + 구독 활성화. (POST /api/portone/billing)
  // 금액·주문명은 서버가 결정(위변조 차단).
  const activateBilling = async (billingKey, fullName, phoneNumber) => {
    try {
      const res = await fetch('/api/portone/billing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          billingKey,
          plan: 'monthly',
          userId: user.id,
          email: user.email,
          // KG이니시스 빌링 청구 필수값(서버가 user_profiles 로 재확인하되 폴백 제공)
          fullName,
          phoneNumber,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.success) {
        alert(t('home.pricing.testPaySuccess'));
      } else {
        console.error('[PortOne] 빌링 결제 실패:', data);
        alert(data.error || t('home.pricing.testPayFailed'));
      }
    } catch (e) {
      console.error('[PortOne] 빌링 결제 오류:', e);
      alert(t('home.pricing.testPayFailed'));
    }
  };

  const runPayTest = async (phone) => {
    if (payTestSubmitting) return;
    setPayTestSubmitting(true);

    const issueId = `lansly-bk-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
    // 이니시스 V2 빌링키 발급도 구매자 이름 필수 → 닉네임 사용(폴백: 메타데이터·이메일)
    const buyerName =
      profile?.updated_display_name ||
      user?.user_metadata?.full_name ||
      user?.user_metadata?.name ||
      user?.email?.split('@')[0] ||
      '고객';
    try {
      // 이니시스 V2 정기결제: 빌링키 발급(카드 등록)만 창에서 진행 → 결제는 서버가 청구.
      // 빌링 채널(INIBillTst) 사용. (발급+결제 결합 방식은 이니시스 V2 미지원)
      const response = await PortOne.requestIssueBillingKey({
        storeId: PORTONE_STORE_ID,
        channelKey: PORTONE_CHANNEL_KEY,
        billingKeyMethod: 'CARD',
        issueId,
        issueName: t('home.pricing.testPayOrderName'),
        // 정기결제창에 결제금액 표시(필수 요건). 실제 승인은 서버가 빌링키로 청구.
        displayAmount: PORTONE_DISPLAY_AMOUNT,
        currency: 'CURRENCY_KRW',
        customer: { fullName: buyerName, email: user.email, phoneNumber: phone },
        // 정기결제 제공기간 — 결제창 우측 '제공기간'에 표시 (월 자동결제).
        offerPeriod: { interval: '1m' },
        // 모바일: 발급 완료 후 이 URL 로 리다이렉트 (아래 useEffect 에서 처리).
        redirectUrl: `${window.location.origin}/?portone=billing`,
      });

      // 데스크톱은 Promise 로 결과 반환. response.code 가 있으면 취소/실패.
      if (response == null || response.code != null) {
        alert(response?.message || t('home.pricing.testPayFailed'));
        return;
      }
      await activateBilling(response.billingKey, buyerName, phone);
    } catch (err) {
      console.error('[PortOne] 결제 요청 에러:', err);
      alert(t('home.pricing.testPayFailed'));
    } finally {
      setPayTestSubmitting(false);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const handleMenuClick = () => {
    setShowPopup(true);
  };

  const handleStartClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      setShowLogin(true);
    }
  };

  const faqs = [
    { q: t('home.faq.q1'), a: t('home.faq.a1') },
    { q: t('home.faq.q2'), a: t('home.faq.a2') },
    { q: t('home.faq.q3'), a: t('home.faq.a3') },
    { q: t('home.faq.q4'), a: t('home.faq.a4') },
    { q: t('home.faq.q5'), a: t('home.faq.a5') },
  ];

  return (
    <div className="gemini-landing">
      {/* 헤더 */}
      <Header
        onMenuClick={handleMenuClick}
        showPopup={showPopup}
        onClosePopup={() => setShowPopup(false)}
        onLoginClick={() => setShowLogin(true)}
      />

      {/* 히어로 섹션 */}
      <section className="hero">
        <div className="container">
          <div className="hero-content">
            <span className="hero-badge">{t('home.hero.badge')}</span>
            {/* 페이지의 유일한 h1 — 검색엔진이 이 화면의 주제로 읽는 문장 */}
            <h1 className="hero-title multiline">{t('home.hero.title')}</h1>
            <p className="hero-description multiline">{t('home.hero.description')}</p>
            <div className="hero-actions">
              <button className="btn btn-primary" onClick={handleStartClick}>
                {t('home.hero.cta')}
              </button>
            </div>
          </div>
          <div className="hero-visual">
            <div className="visual-circle"></div>
            <img
              src="/images/dashboard-hero.png"
              alt="Lansly Dashboard Preview"
              className="glass-card-image"
            />
          </div>
        </div>
      </section>

      {/* 프로모션 배너 */}
      {/* <PromoBanner className="promo-banner-home" /> */}

      {/* 문제 제기 섹션 */}
      <section className="problem">
        <div className="container">
          <h2 className="section-title multiline">{t('home.problem.title')}</h2>
          <div className="problem-grid">
            <div className="problem-card">
              <div className="problem-icon">
                <ClockIcon />
              </div>
              <h3>{t('home.problem.card1Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.problem.card1Body')}</p>
            </div>
            <div className="problem-card">
              <div className="problem-icon">
                <EyeIcon />
              </div>
              <h3>{t('home.problem.card2Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.problem.card2Body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 솔루션 섹션 */}
      <section className="solution">
        <div className="container">
          <div className="feature-row">
            <div className="feature-text">
              <span className="feature-label">{t('home.solution.planningLabel')}</span>
              <h3 className="multiline">{t('home.solution.planningTitle')}</h3>
              <p className="multiline">{t('home.solution.planningBody')}</p>
            </div>
            <div className="feature-visual calendar-visual">
              <img
                src="/images/calendar-feature.png"
                alt={t('home.solution.planningAlt')}
                className="calendar-image"
              />
            </div>
          </div>

          <div className="feature-row reverse">
            <div className="feature-text">
              <span className="feature-label">{t('home.solution.transparencyLabel')}</span>
              <h3 className="multiline">{t('home.solution.transparencyTitle')}</h3>
              <p className="multiline">{t('home.solution.transparencyBody')}</p>
            </div>
            <div className="feature-visual dashboard-visual">
              <img
                src="/images/dashboard-feature.png"
                alt={t('home.solution.transparencyAlt')}
                className="dashboard-image"
              />
            </div>
          </div>

          <div className="feature-row">
            <div className="feature-text">
              <span className="feature-label">{t('home.solution.communicationLabel')}</span>
              <h3 className="multiline">{t('home.solution.communicationTitle')}</h3>
              <p className="multiline">{t('home.solution.communicationBody')}</p>
            </div>
            <div className="feature-visual todo-visual">
              <div className="todo-item checked">{t('home.solution.todo1')}</div>
              <div className="todo-item checked">{t('home.solution.todo2')}</div>
              <div className="todo-item">{t('home.solution.todo3')}</div>
            </div>
          </div>
        </div>
      </section>

      {/* 어떻게 작동하나요 섹션 */}
      <section className="how-it-works">
        <div className="container">
          <h2>{t('home.howItWorks.title')}</h2>
          <div className="steps">
            <div className="step">
              <div className="step-number">1</div>
              <h3>{t('home.howItWorks.step1Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.howItWorks.step1Body')}</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">2</div>
              <h3>{t('home.howItWorks.step2Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.howItWorks.step2Body')}</p>
            </div>
            <div className="step-arrow">→</div>
            <div className="step">
              <div className="step-number">3</div>
              <h3>{t('home.howItWorks.step3Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.howItWorks.step3Body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 타겟 오디언스 섹션 */}
      <section className="target">
        <div className="container">
          <h2 className="section-title">{t('home.target.title')}</h2>
          <div className="target-grid">
            <div className="target-card">
              <div className="target-icon">
                <LaptopIcon />
              </div>
              <h3>{t('home.target.card1Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.target.card1Body')}</p>
            </div>
            <div className="target-card">
              <div className="target-icon">
                <BuildingIcon />
              </div>
              <h3>{t('home.target.card2Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.target.card2Body')}</p>
            </div>
            <div className="target-card">
              <div className="target-icon">
                <RocketIcon />
              </div>
              <h3>{t('home.target.card3Title')}</h3>
              <p className="home-lead-copy multiline">{t('home.target.card3Body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* 생태계(출시 예정 서비스) 섹션 */}
      <section className="ecosystem">
        <div className="container">
          <h2 className="ecosystem-title">{t('home.ecosystem.title')}</h2>
          <p className="ecosystem-sub">{t('home.ecosystem.subtitle')}</p>
          <div className="ecosystem-grid">
            <div className="ecosystem-card">
              <div className="ecosystem-card-top">
                <div className="ecosystem-icon">
                  <BriefcaseIcon />
                </div>
                <span className="ecosystem-badge">{t('home.ecosystem.comingSoon')}</span>
              </div>
              <h3 className="ecosystem-card-title">{t('home.ecosystem.card1Title')}</h3>
              <p>{t('home.ecosystem.card1Body')}</p>
            </div>
            <div className="ecosystem-card">
              <div className="ecosystem-card-top">
                <div className="ecosystem-icon">
                  <TagIcon />
                </div>
                <span className="ecosystem-badge">{t('home.ecosystem.comingSoon')}</span>
              </div>
              <h3 className="ecosystem-card-title">{t('home.ecosystem.card2Title')}</h3>
              <p>{t('home.ecosystem.card2Body')}</p>
            </div>
            <div className="ecosystem-card">
              <div className="ecosystem-card-top">
                <div className="ecosystem-icon">
                  <GraduationCapIcon />
                </div>
                <span className="ecosystem-badge">{t('home.ecosystem.comingSoon')}</span>
              </div>
              <h3 className="ecosystem-card-title">{t('home.ecosystem.card3Title')}</h3>
              <p>{t('home.ecosystem.card3Body')}</p>
            </div>
            <div className="ecosystem-card">
              <div className="ecosystem-card-top">
                <div className="ecosystem-icon">
                  <TargetIcon />
                </div>
                <span className="ecosystem-badge">{t('home.ecosystem.comingSoon')}</span>
              </div>
              <h3 className="ecosystem-card-title">{t('home.ecosystem.card4Title')}</h3>
              <p>{t('home.ecosystem.card4Body')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing 섹션 */}
      <section className="pricing">
        <div className="container">
          <h2>{t('home.pricing.title')}</h2>
          <p className="section-sub">{t('home.pricing.subtitle')}</p>

          {/* 결제 주기 토글 — 카드사 심사용으로 임시 숨김(SHOW_YEARLY) */}
          {SHOW_YEARLY && (
            <div className="billing-toggle">
              <span className={billingInterval === 'monthly' ? 'active' : ''}>
                {t('home.pricing.monthly')}
              </span>
              <button
                className={`toggle-switch ${billingInterval === 'yearly' ? 'yearly' : ''}`}
                onClick={() =>
                  setBillingInterval(billingInterval === 'monthly' ? 'yearly' : 'monthly')
                }
                aria-label={t('home.pricing.toggleAria')}
              >
                <span className="toggle-slider"></span>
              </button>
              <span className={billingInterval === 'yearly' ? 'active' : ''}>
                {t('home.pricing.yearly')}{' '}
                <span className="discount-badge">{t('home.pricing.discountBadge')}</span>
              </span>
            </div>
          )}

          <div className="pricing-grid">
            <div className="pricing-card">
              <div className="pricing-badge">{t('home.pricing.freeName')}</div>
              <div className="pricing-price">{t('home.pricing.freePrice')}</div>
              <p className="pricing-period">{t('home.pricing.freePeriod')}</p>
              <ul>
                <li>{t('home.pricing.featProjectFree')}</li>
                <li>{t('home.pricing.featScheduling')}</li>
                <li>{t('home.pricing.featDashboard')}</li>
                <li>{t('home.pricing.featInviteFree')}</li>
              </ul>
              <button className="btn-outline" onClick={handleStartClick}>
                {t('home.pricing.freeCta')}
              </button>
            </div>
            <div className="pricing-card featured">
              <div className="pricing-badge">{t('home.pricing.premiumName')}</div>
              <div className="pricing-price">
                <span
                  className={`pricing-original ${billingInterval === 'yearly' ? 'visible' : ''}`}
                >
                  ₩12,000
                </span>
                {billingInterval === 'yearly' ? '₩9,500' : '₩12,000'}
              </div>
              <p className="pricing-period">
                {billingInterval === 'yearly'
                  ? t('home.pricing.premiumPeriodYearly')
                  : t('home.pricing.premiumPeriodMonthly')}
              </p>
              <div
                className={`pricing-discount-text ${billingInterval === 'yearly' ? 'visible' : ''}`}
              >
                {t('home.pricing.yearlyDiscountText')}
              </div>
              <ul>
                <li>{t('home.pricing.featProjectPremium')}</li>
                <li>{t('home.pricing.featScheduling')}</li>
                <li>{t('home.pricing.featDashboard')}</li>
                <li>{t('home.pricing.featInvitePremium')}</li>
              </ul>
              <button className="btn-primary" onClick={handlePremiumClick}>
                {t('home.pricing.premiumCta')}
              </button>

              {/* 포트원 KG이니시스 정기결제 (빌링키) */}
              <button
                type="button"
                className="portone-test-btn"
                onClick={handlePortonePayTest}
                disabled={payTestSubmitting}
              >
                {t('home.pricing.testPayCta')}
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ 섹션 */}
      <section className="faq">
        <div className="container">
          <h2>{t('home.faq.title')}</h2>
          <div className="faq-list">
            {faqs.map((faq, index) => (
              <div
                key={index}
                className={`faq-item ${openFaq === index ? 'open' : ''}`}
                onClick={() => toggleFaq(index)}
              >
                <div className="faq-question">
                  <span>{faq.q}</span>
                  <span className="faq-toggle">{openFaq === index ? '−' : '+'}</span>
                </div>
                {openFaq === index && (
                  <div className="faq-answer" style={{ whiteSpace: 'pre-line' }}>
                    {faq.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* 하단 CTA 섹션 */}
      <section className="cta-section">
        <div className="cta-bg-glow"></div>
        <div className="container">
          <h2 className="multiline">{t('home.cta.title')}</h2>
          <button className="btn btn-primary large" onClick={handleStartClick}>
            {t('home.cta.button')}
          </button>
        </div>
      </section>

      {/* 푸터 */}
      <Footer />

      {/* 로그인 모달 */}
      <Login isOpen={showLogin} onClose={() => setShowLogin(false)} />

      {showPhonePopup && (
        <PhoneNumberPopup
          onClose={() => setShowPhonePopup(false)}
          onSaved={(phone) => {
            setShowPhonePopup(false);
            runPayTest(phone);
          }}
        />
      )}
    </div>
  );
}

export default GeminiPage;
